import { useEffect, useMemo, useState } from "react";
import { useCustomerShortCodes } from "@/lib/utils/customer/useCustomerShortCodes";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2, Package, PackageOpen } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { useDriverAssignmentDetail } from "@/hooks/useDriverAssignmentDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EnvironmentSwitcher,
  useEnvironmentBorderClass,
} from "@/components/layout/EnvironmentSwitcher";
import { toast } from "sonner";
import { NeedsServiceToggle } from "@/components/yard/NeedsServiceToggle";
import { YardPhotoCapture } from "@/components/yard/YardPhotoCapture";
import { uploadTrailerEventPhoto } from "@/components/yard/yardPhotoUtils";
import { ensureTrailerExists } from "@/lib/trailer-utils";

/**
 * Driver-only "Verify trailer information" page. Mirrors the AYMS manual
 * check-in fields, minus the back-of-trailer + BOL photo captures (those
 * live on the readiness checklist). Saves changes to the `trailers` row
 * and writes a lightweight `driver_verified` trailer_event used as the
 * source of truth for the "verified" checkmark on the assignment screen.
 */
export default function DriverVerifyTrailer() {
  const { id: legId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else if (legId) {
      navigate(`/my-assignments/${legId}`);
    } else {
      navigate("/my-assignments");
    }
  };
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const envBorderClass = useEnvironmentBorderClass();
  const { leg, isLoading } = useDriverAssignmentDetail(legId);
  const { data: trailerTypes } = useTrailerTypes();

  const trailerIdText = (leg as { trailer_id?: string | null } | null)?.trailer_id ?? "";

  const { data: trailerRow, refetch: refetchTrailerRow } = useQuery({
    queryKey: ["trailer-by-id", trailerIdText, tenantId],
    enabled: !!trailerIdText && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("id, trailer_id, trailer_type_id, scac, seal_id, seal_status, load_status, needs_service, needs_service_reason")
        .eq("trailer_id", trailerIdText)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data;
    },
  });

  const { data: tractors = [] } = useQuery({
    queryKey: ["tractors-lookup"],
    queryFn: async () => {
      const { data } = await supabase.from("tractors").select("id, name").order("name");
      return data ?? [];
    },
  });

  const { data: customerShortCodes = [] } = useCustomerShortCodes();

  // Form state
  const [truckOwner, setTruckOwner] = useState<"aurora" | "partner" | "">("");
  const [truckId, setTruckId] = useState("");
  const [trailerTypeId, setTrailerTypeId] = useState("");
  const [scac, setScac] = useState("");
  const [loadStatus, setLoadStatus] = useState<"empty" | "loaded" | "">("");
  const [sealId, setSealId] = useState("");
  const [sealStatus, setSealStatus] = useState<"intact" | "broken" | "">("");
  const [needsService, setNeedsService] = useState(false);
  const [needsServiceReason, setNeedsServiceReason] = useState("");
  const [pendingSealPhoto, setPendingSealPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  // Pre-fill from trailer row & assigned tractor
  useEffect(() => {
    if (!trailerRow) return;
    if (trailerRow.trailer_type_id) setTrailerTypeId(trailerRow.trailer_type_id);
    if (trailerRow.scac) setScac(trailerRow.scac);
    if (trailerRow.seal_id) setSealId(trailerRow.seal_id);
    if (trailerRow.seal_status === "intact" || trailerRow.seal_status === "broken") {
      setSealStatus(trailerRow.seal_status);
    }
    if (trailerRow.load_status === "empty" || trailerRow.load_status === "loaded") {
      setLoadStatus(trailerRow.load_status as "empty" | "loaded");
    }
    if ((trailerRow as { needs_service?: boolean | null }).needs_service) {
      setNeedsService(true);
      setNeedsServiceReason(
        (trailerRow as { needs_service_reason?: string | null }).needs_service_reason ?? "",
      );
    }
  }, [trailerRow]);

  useEffect(() => {
    const assigned = (leg as { assigned_tractor?: { id: string; name: string } | null } | null)?.assigned_tractor;
    if (assigned?.name && !truckId) {
      setTruckId(assigned.name);
      const isFleet = tractors.some((t) => t.name === assigned.name);
      setTruckOwner(isFleet ? "aurora" : "partner");
    }
  }, [leg, tractors, truckId]);

  const isReady = useMemo(
    () =>
      truckId.trim().length > 0 &&
      truckOwner !== "" &&
      trailerTypeId !== "" &&
      scac.trim().length > 0 &&
      loadStatus !== "" &&
      (loadStatus !== "loaded" ||
        (sealId.trim().length > 0 && sealStatus !== "" && !!pendingSealPhoto)) &&
      (!needsService || needsServiceReason.trim().length > 0),
    [
      truckId,
      truckOwner,
      trailerTypeId,
      scac,
      loadStatus,
      sealId,
      sealStatus,
      pendingSealPhoto,
      needsService,
      needsServiceReason,
    ],
  );

  const handleSave = async () => {
    if (!isReady || !trailerIdText.trim() || !tenantId) return;
    setSaving(true);
    try {
      // 0) The trailer may not have a record yet (e.g. it only ever existed as
      // free text on the order). Provision it so verification isn't blocked.
      let trailerDbId = trailerRow?.id ?? null;
      if (!trailerDbId) {
        const ensured = await ensureTrailerExists({
          trailerId: trailerIdText.trim(),
          trailerTypeId: trailerTypeId || null,
          tenantId,
        });
        if (!ensured.ok) {
          toast.error(
            ensured.reason === "permission_denied"
              ? `You don't have permission to add trailer ${trailerIdText.trim()}. Ask dispatch to add it, then try again.`
              : `Couldn't create a record for trailer ${trailerIdText.trim()}. Please contact dispatch.`,
          );
          return;
        }
        const { data: refreshed } = await refetchTrailerRow();
        trailerDbId = refreshed?.id ?? null;
        if (!trailerDbId) {
          toast.error(
            `Trailer ${trailerIdText.trim()} isn't set up for your site yet. Ask dispatch to add it, then try again.`,
          );
          return;
        }
      }

      // 1) Update trailer record (mirror checkInTrailer's needs-service writes)
      const trailerUpdate: Record<string, unknown> = {
        trailer_type_id: trailerTypeId,
        scac: scac.trim().toUpperCase(),
        seal_id: loadStatus === "loaded" ? sealId.trim() : null,
        seal_status: loadStatus === "loaded" ? sealStatus || null : null,
        load_status: loadStatus,
        needs_service: needsService,
        needs_service_reason: needsService ? needsServiceReason.trim() || null : null,
        needs_service_set_at: needsService ? new Date().toISOString() : null,
        needs_service_set_by: needsService ? user?.id ?? null : null,
      };
      const { error: updErr } = await supabase
        .from("trailers")
        .update(trailerUpdate as never)
        .eq("id", trailerDbId);
      if (updErr) throw updErr;

      // 2) Lightweight driver_verified event (no yard status change)
      const eventPayload: Record<string, unknown> = {
        trailer_id: trailerDbId,
        event_type: "driver_verified",
        created_by_user_id: user?.id,
        truck_id: truckId.trim() || null,
        seal_id: loadStatus === "loaded" ? sealId.trim() : null,
        seal_status: loadStatus === "loaded" ? sealStatus || null : null,
        load_status: loadStatus || null,
        tenant_id: tenantId,
        needs_service: needsService,
        needs_service_reason: needsService ? needsServiceReason.trim() || null : null,
        notes: "Driver verified from assignment screen",
        // DTP-110609: scope the verification to this leg so a reused trailer
        // never inherits an earlier job's answer.
        leg_id: legId ?? null,
      };
      const { data: evtRow, error: evtErr } = await supabase
        .from("trailer_events")
        .insert(eventPayload as never)
        .select("id")
        .single();
      if (evtErr) throw evtErr;

      // 3) Upload the seal photo when loaded
      if (loadStatus === "loaded" && pendingSealPhoto && evtRow?.id) {
        try {
          await uploadTrailerEventPhoto({
            eventId: evtRow.id,
            trailerDbId: trailerDbId,
            file: pendingSealPhoto,
            photoType: "seal",
            tenantId,
            userId: user?.id ?? null,
          });
        } catch (photoErr) {
          console.error("Seal photo upload failed", photoErr);
          toast.error("Trailer verified, but seal photo failed to upload");
        }
      }

      toast.success("Trailer information verified");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save trailer information");
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <header className={`sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-card px-3 ${envBorderClass}`}>
      <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="flex-1" />
      <EnvironmentSwitcher variant="header" />
    </header>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {header}
        <main className="p-4 text-center text-sm text-muted-foreground">Loading…</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {header}
      <div className="sticky top-14 z-10 bg-background border-b">
        <div className="max-w-lg mx-auto px-4 py-3 space-y-0.5">
          <p className="text-xs text-muted-foreground truncate">{leg?.unique_id ?? ""}</p>
          <h1 className="text-2xl font-bold text-foreground">Verify trailer information</h1>
        </div>
      </div>
      <main className="p-4 pb-24">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Trailer ID (read-only) */}
          <div className="space-y-1.5">
            <Label className="text-xs">Trailer ID</Label>
            <Input value={trailerIdText} readOnly disabled className="font-mono" />
          </div>

          {/* Truck owner */}
          <div className="space-y-1.5">
            <Label className="text-xs">Truck Owner *</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={truckOwner === "aurora" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (truckOwner !== "aurora") setTruckId("");
                  setTruckOwner("aurora");
                }}
              >
                Aurora
              </Button>
              <Button
                type="button"
                variant={truckOwner === "partner" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (truckOwner !== "partner") setTruckId("");
                  setTruckOwner("partner");
                }}
              >
                Partner
              </Button>
            </div>
          </div>

          {/* Truck ID */}
          <div className="space-y-1.5">
            <Label className="text-xs">Truck ID *</Label>
            {truckOwner === "partner" ? (
              <Input
                placeholder="Enter truck ID..."
                value={truckId}
                onChange={(e) => setTruckId(e.target.value)}
              />
            ) : (
              <Select value={truckId} onValueChange={setTruckId} disabled={!truckOwner}>
                <SelectTrigger>
                  <SelectValue placeholder={truckOwner ? "Select truck..." : "Select owner first"} />
                </SelectTrigger>
                <SelectContent>
                  {tractors.map((t) => (
                    <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Trailer type */}
          <div className="space-y-1.5">
            <Label className="text-xs">Trailer Type *</Label>
            <Select value={trailerTypeId} onValueChange={setTrailerTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select trailer type" />
              </SelectTrigger>
              <SelectContent>
                {trailerTypes?.map((tt) => (
                  <SelectItem key={tt.id} value={tt.id}>{tt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* SCAC */}
          <div className="space-y-1.5">
            <Label className="text-xs">SCAC *</Label>
            <Select value={scac} onValueChange={setScac}>
              <SelectTrigger>
                <SelectValue placeholder="Select SCAC" />
              </SelectTrigger>
              <SelectContent>
                {customerShortCodes.map((c) => (
                  <SelectItem key={c.short_code} value={c.short_code}>
                    <span className="font-mono">{c.short_code}</span>
                    {c.organization_name ? <span className="text-muted-foreground"> — {c.organization_name}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Load status */}
          <div className="space-y-1.5">
            <Label className="text-xs">Load Status *</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={loadStatus === "empty" ? "default" : "outline"}
                size="sm"
                onClick={() => setLoadStatus("empty")}
              >
                <PackageOpen className="h-4 w-4 mr-2" />
                Empty
              </Button>
              <Button
                type="button"
                variant={loadStatus === "loaded" ? "default" : "outline"}
                size="sm"
                onClick={() => setLoadStatus("loaded")}
              >
                <Package className="h-4 w-4 mr-2" />
                Loaded
              </Button>
            </div>
          </div>

          {loadStatus === "loaded" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Seal ID *</Label>
                <Input
                  placeholder="Enter seal number..."
                  value={sealId}
                  onChange={(e) => setSealId(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Seal Status *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={sealStatus === "intact" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSealStatus("intact")}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Intact
                  </Button>
                  <Button
                    type="button"
                    variant={sealStatus === "broken" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSealStatus("broken")}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Broken
                  </Button>
                </div>
              </div>
              <YardPhotoCapture
                photo={pendingSealPhoto}
                onPhotoChange={setPendingSealPhoto}
                label="Seal Photo *"
                captureLabel={pendingSealPhoto ? "Retake Seal Photo" : "Take Seal Photo"}
              />
            </>
          )}

          {/* Needs Service — mirrors AYMS check-in */}
          <NeedsServiceToggle
            needsService={needsService}
            needsServiceReason={needsServiceReason}
            onNeedsServiceChange={setNeedsService}
            onReasonChange={setNeedsServiceReason}
          />
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-3 z-20">
        <div className="max-w-lg mx-auto">
          <Button className="w-full" size="lg" onClick={handleSave} disabled={!isReady || saving || !trailerIdText.trim()}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              "Confirm details"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}